import { useCallback } from "react";

import {
  ComplianceService,
  type Iso42001MaturityResponse,
  type MaturityLevelSummary,
  type NistMaturityResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatMaturityLevel } from "@/lib/compliance-helpers";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

export function ComplianceMaturityTab() {
  const { t } = useTranslation();
  const nistFetcher = useCallback(
    () => ComplianceService.getNistMaturity(),
    []
  );
  const isoFetcher = useCallback(
    () => ComplianceService.getIso42001Maturity(),
    []
  );

  const nist = useApi<NistMaturityResponse>(nistFetcher);
  const iso = useApi<Iso42001MaturityResponse>(isoFetcher);

  return (
    <div className="space-y-6">
      <Section
        title={t("compliance.maturity.nistTitle")}
        description={t("compliance.maturity.nistDescription")}
      >
        {nist.error && (
          <Alert variant="warning">{parseApiError(nist.error)}</Alert>
        )}
        {nist.isLoading && !nist.data && <Skeleton className="h-40 w-full" />}
        {nist.data && (
          <MaturityCard
            overall={nist.data.overall_maturity}
            interpretation={nist.data.interpretation}
            buckets={nist.data.function_maturities}
            bucketLabel={t("compliance.maturity.functionLabel")}
          />
        )}
      </Section>

      <Section
        title={t("compliance.maturity.isoTitle")}
        description={t("compliance.maturity.isoDescription")}
      >
        {iso.error && (
          <Alert variant="warning">{parseApiError(iso.error)}</Alert>
        )}
        {iso.isLoading && !iso.data && <Skeleton className="h-40 w-full" />}
        {iso.data && (
          <MaturityCard
            overall={iso.data.overall_maturity}
            interpretation={iso.data.interpretation}
            buckets={iso.data.phase_maturities}
            bucketLabel={t("compliance.maturity.phaseLabel")}
          />
        )}
      </Section>
    </div>
  );
}

function MaturityCard({
  overall,
  interpretation,
  buckets,
  bucketLabel,
}: {
  overall: number;
  interpretation: string;
  buckets: Record<string, MaturityLevelSummary>;
  bucketLabel: string;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(buckets);
  return (
    <div className="rounded-lg border border-ocean-100 bg-white p-5">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
        <div className="tablet:col-span-1">
          <div className="label-eyebrow">
            {t("compliance.maturity.overallLabel")}
          </div>
          <div className="mt-1 text-2xl font-medium text-ocean-900">
            {formatMaturityLevel(overall)}
          </div>
          <p className="mt-2 text-xs text-neutral-600">{interpretation}</p>
        </div>
        <div className="tablet:col-span-2">
          <div className="label-eyebrow mb-2">{bucketLabel}</div>
          <div className="grid grid-cols-1 gap-2 tablet:grid-cols-2">
            {entries.map(([key, summary]) => (
              <div
                key={key}
                className="rounded-md border border-ocean-100 bg-ocean-50/40 px-3 py-2"
              >
                <div className="text-xs font-medium text-ocean-700">{key}</div>
                <div className="mt-0.5 flex items-baseline justify-between gap-2">
                  <span className="text-sm text-ocean-900">
                    {formatMaturityLevel(summary.level)}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {t("compliance.maturity.coverage", {
                      pct: summary.coverage_pct.toFixed(0),
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
